import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Navigation, User, Clock, ArrowLeft, Calendar, Plus, Search, Locate, X, Filter, SlidersHorizontal, DollarSign, AlertCircle, ArrowRight } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Major cities with coordinates for long-distance suggestions
const MAJOR_CITIES = [
  { name: 'Dallas, TX', lat: 32.7767, lng: -96.7970 },
  { name: 'Houston, TX', lat: 29.7604, lng: -95.3698 },
  { name: 'Austin, TX', lat: 30.2672, lng: -97.7431 },
  { name: 'San Antonio, TX', lat: 29.4241, lng: -98.4936 },
  { name: 'Oklahoma City, OK', lat: 35.4676, lng: -97.5164 },
  { name: 'Tulsa, OK', lat: 36.1540, lng: -95.9928 },
  { name: 'Kansas City, MO', lat: 39.0997, lng: -94.5786 },
  { name: 'Denver, CO', lat: 39.7392, lng: -104.9903 },
  { name: 'Wichita, KS', lat: 37.6872, lng: -97.3301 },
  { name: 'Amarillo, TX', lat: 35.2220, lng: -101.8313 },
  { name: 'Lubbock, TX', lat: 33.5779, lng: -101.8552 },
  { name: 'Little Rock, AR', lat: 34.7465, lng: -92.2896 },
  { name: 'Memphis, TN', lat: 35.1495, lng: -90.0490 },
  { name: 'St. Louis, MO', lat: 38.6270, lng: -90.1994 },
  { name: 'Albuquerque, NM', lat: 35.0844, lng: -106.6504 },
];

// Calculate distance in miles using Haversine formula
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distanceKm = R * c;
  return distanceKm / 1.6; // Convert to miles
};

// Calculate default fare based on Wichita pricing: $3 min for <3 miles, $1/mile rounded up for 3+ miles, +$1 per extra seat
const calculateFare = (distance, seats = 1) => {
  let baseFare;
  if (distance < 3) {
    baseFare = 3; // Minimum $3 for rides under 3 miles
  } else {
    baseFare = Math.ceil(distance); // Round up to nearest dollar for 3+ miles
  }
  // Add $1 for each extra seat beyond the first
  const extraSeats = Math.max(0, seats - 1);
  return Math.round(baseFare + extraSeats); // Always return whole number
};

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function MapUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 13);
    }
  }, [center, map]);
  return null;
}

export default function RidesAvailable() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rides, setRides] = useState([]);
  const [filteredRides, setFilteredRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPostRide, setShowPostRide] = useState(false);
  const [mapCenter, setMapCenter] = useState([37.6872, -97.3301]);
  
  // Location filter state
  const [userLocation, setUserLocation] = useState(null);
  const [filterPickup, setFilterPickup] = useState('');
  const [filterPickupCoords, setFilterPickupCoords] = useState(null);
  const [filterDropoff, setFilterDropoff] = useState('');
  const [filterDropoffCoords, setFilterDropoffCoords] = useState(null);
  const [filterRadius, setFilterRadius] = useState(50); // Default 50 miles
  const [filterPickupSuggestions, setFilterPickupSuggestions] = useState([]);
  const [filterDropoffSuggestions, setFilterDropoffSuggestions] = useState([]);
  const [showFilterPickupSuggestions, setShowFilterPickupSuggestions] = useState(false);
  const [showFilterDropoffSuggestions, setShowFilterDropoffSuggestions] = useState(false);
  const filterPickupTimeoutRef = useRef(null);
  const filterDropoffTimeoutRef = useRef(null);
  
  // Long-distance ride suggestions
  const [suggestedRides, setSuggestedRides] = useState([]);
  const [showLongDistanceInfo, setShowLongDistanceInfo] = useState(false);
  const [searchDistance, setSearchDistance] = useState(0);
  
  const [rideForm, setRideForm] = useState({
    pickup_address: '',
    pickup_lat: 37.6872,
    pickup_lng: -97.3301,
    dropoff_address: '',
    dropoff_lat: 37.7200,
    dropoff_lng: -97.2800,
    ride_type: 'now',
    scheduled_time: '',
    notes: '',
    seats_available: 1,
    seats_requested: 1,
    custom_price: '',
  });
  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [dropoffSuggestions, setDropoffSuggestions] = useState([]);
  const [showPickupSuggestions, setShowPickupSuggestions] = useState(false);
  const [showDropoffSuggestions, setShowDropoffSuggestions] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [selectedRide, setSelectedRide] = useState(null);
  const [seatsToJoin, setSeatsToJoin] = useState(1);

  useEffect(() => {
    fetchRides();
    
    // Get user's current location for filtering
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setMapCenter([position.coords.latitude, position.coords.longitude]);
        },
        () => {
          // Default to Wichita if location access denied
          setUserLocation({ latitude: 37.6872, longitude: -97.3301 });
        }
      );
    }
  }, []);

  // Auto-calculate and fill the price when locations or seats change
  useEffect(() => {
    if (rideForm.pickup_lat && rideForm.dropoff_lat && 
        rideForm.pickup_address && rideForm.dropoff_address) {
      const distance = calculateDistance(
        rideForm.pickup_lat,
        rideForm.pickup_lng,
        rideForm.dropoff_lat,
        rideForm.dropoff_lng
      );
      const seats = user.role === 'driver' ? rideForm.seats_available : rideForm.seats_requested;
      const defaultFare = calculateFare(distance, seats);
      setRideForm(prev => ({
        ...prev,
        custom_price: String(defaultFare)
      }));
    }
  }, [rideForm.pickup_lat, rideForm.pickup_lng, rideForm.dropoff_lat, rideForm.dropoff_lng, rideForm.seats_available, rideForm.seats_requested, user.role]);

  // Apply filters when rides, filter criteria, or user location changes
  useEffect(() => {
    let filtered = [...rides];
    let suggested = [];
    let distance = 0;
    
    // Calculate search distance if both pickup and dropoff are set
    if (filterPickupCoords && filterDropoffCoords) {
      distance = calculateDistance(
        filterPickupCoords.lat,
        filterPickupCoords.lng,
        filterDropoffCoords.lat,
        filterDropoffCoords.lng
      );
      setSearchDistance(distance);
    } else {
      setSearchDistance(0);
    }
    
    // Filter by pickup location proximity
    if (filterPickupCoords) {
      filtered = filtered.filter(ride => {
        const dist = calculateDistance(
          filterPickupCoords.lat,
          filterPickupCoords.lng,
          ride.pickup_lat,
          ride.pickup_lng
        );
        return dist <= filterRadius;
      });
    }
    
    // Filter by dropoff location proximity
    if (filterDropoffCoords) {
      filtered = filtered.filter(ride => {
        const dist = calculateDistance(
          filterDropoffCoords.lat,
          filterDropoffCoords.lng,
          ride.dropoff_lat,
          ride.dropoff_lng
        );
        return dist <= filterRadius;
      });
    }
    
    // For long-distance rides (>50 miles), find related rides near destination city
    if (distance > 50 && filterDropoffCoords && filtered.length === 0) {
      setShowLongDistanceInfo(true);
      
      // Find the closest major city to the destination
      let closestCity = null;
      let closestDistance = Infinity;
      
      MAJOR_CITIES.forEach(city => {
        const dist = calculateDistance(
          filterDropoffCoords.lat,
          filterDropoffCoords.lng,
          city.lat,
          city.lng
        );
        if (dist < closestDistance) {
          closestDistance = dist;
          closestCity = city;
        }
      });
      
      // Find rides going near the destination city (within 100 miles)
      if (closestCity) {
        suggested = rides.filter(ride => {
          const distToDestCity = calculateDistance(
            closestCity.lat,
            closestCity.lng,
            ride.dropoff_lat,
            ride.dropoff_lng
          );
          return distToDestCity <= 100;
        }).map(ride => ({
          ...ride,
          closestCity: closestCity.name,
          distanceToDestination: calculateDistance(
            ride.dropoff_lat,
            ride.dropoff_lng,
            filterDropoffCoords.lat,
            filterDropoffCoords.lng
          )
        })).sort((a, b) => a.distanceToDestination - b.distanceToDestination);
      }
    } else {
      setShowLongDistanceInfo(false);
    }
    
    setFilteredRides(filtered);
    setSuggestedRides(suggested);
  }, [rides, filterPickupCoords, filterDropoffCoords, filterRadius]);

  const applyFilters = () => {
    let filtered = [...rides];
    
    // Filter by pickup location proximity
    if (filterPickupCoords) {
      filtered = filtered.filter(ride => {
        const distance = calculateDistance(
          filterPickupCoords.lat,
          filterPickupCoords.lng,
          ride.pickup_lat,
          ride.pickup_lng
        );
        return distance <= filterRadius;
      });
    }
    
    // Filter by dropoff location proximity
    if (filterDropoffCoords) {
      filtered = filtered.filter(ride => {
        const distance = calculateDistance(
          filterDropoffCoords.lat,
          filterDropoffCoords.lng,
          ride.dropoff_lat,
          ride.dropoff_lng
        );
        return distance <= filterRadius;
      });
    }
    
    setFilteredRides(filtered);
  };

  const searchFilterLocation = async (query, type) => {
    if (!query || query.length < 3) {
      if (type === 'pickup') setFilterPickupSuggestions([]);
      else setFilterDropoffSuggestions([]);
      return;
    }

    try {
      // Get user's current location for proximity-based search
      const centerLat = userLocation?.latitude || mapCenter[0] || 37.6872;
      const centerLng = userLocation?.longitude || mapCenter[1] || -97.3301;
      
      // Create a ~50 mile (~80km) bounding box around user's location
      const latOffset = 0.75; // ~50 miles north/south
      const lngOffset = 0.9;  // ~50 miles east/west
      
      // viewbox format: left,top,right,bottom (minLon, maxLat, maxLon, minLat)
      const viewbox = `${centerLng - lngOffset},${centerLat + latOffset},${centerLng + lngOffset},${centerLat - latOffset}`;
      
      const response = await axios.get(`https://nominatim.openstreetmap.org/search`, {
        params: {
          q: query,
          format: 'json',
          limit: 5,
          addressdetails: 1,
          viewbox: viewbox,
          bounded: 1, // Strictly limit results to within the viewbox
        },
        headers: {
          'User-Agent': 'vgoride-App/1.0'
        }
      });

      let suggestions = response.data.map(item => ({
        display_name: item.display_name,
        lat: parseFloat(item.lat),
        lon: parseFloat(item.lon),
      }));

      // If no results found in the bounded area, search without bounds as fallback
      if (suggestions.length === 0) {
        const fallbackResponse = await axios.get(`https://nominatim.openstreetmap.org/search`, {
          params: {
            q: query,
            format: 'json',
            limit: 5,
            addressdetails: 1,
          },
          headers: {
            'User-Agent': 'vgoride-App/1.0'
          }
        });
        suggestions = fallbackResponse.data.map(item => ({
          display_name: item.display_name,
          lat: parseFloat(item.lat),
          lon: parseFloat(item.lon),
        }));
      }

      if (type === 'pickup') {
        setFilterPickupSuggestions(suggestions);
        setShowFilterPickupSuggestions(true);
      } else {
        setFilterDropoffSuggestions(suggestions);
        setShowFilterDropoffSuggestions(true);
      }
    } catch (error) {
      console.error('Failed to search location:', error);
    }
  };

  const handleFilterPickupChange = (value) => {
    setFilterPickup(value);
    if (filterPickupTimeoutRef.current) clearTimeout(filterPickupTimeoutRef.current);
    filterPickupTimeoutRef.current = setTimeout(() => searchFilterLocation(value, 'pickup'), 500);
  };

  const handleFilterDropoffChange = (value) => {
    setFilterDropoff(value);
    if (filterDropoffTimeoutRef.current) clearTimeout(filterDropoffTimeoutRef.current);
    filterDropoffTimeoutRef.current = setTimeout(() => searchFilterLocation(value, 'dropoff'), 500);
  };

  const selectFilterPickupSuggestion = (suggestion) => {
    setFilterPickup(suggestion.display_name);
    setFilterPickupCoords({ lat: suggestion.lat, lng: suggestion.lon });
    setShowFilterPickupSuggestions(false);
    setFilterPickupSuggestions([]);
  };

  const selectFilterDropoffSuggestion = (suggestion) => {
    setFilterDropoff(suggestion.display_name);
    setFilterDropoffCoords({ lat: suggestion.lat, lng: suggestion.lon });
    setShowFilterDropoffSuggestions(false);
    setFilterDropoffSuggestions([]);
  };

  const clearFilters = () => {
    setFilterPickup('');
    setFilterPickupCoords(null);
    setFilterDropoff('');
    setFilterDropoffCoords(null);
    setFilterRadius(50);
    setFilteredRides(rides);
  };

  // Calculate default fare based on current form inputs
  const calculateDefaultFare = () => {
    if (!rideForm.pickup_lat || !rideForm.dropoff_lat) return '3';
    const distance = calculateDistance(
      rideForm.pickup_lat, 
      rideForm.pickup_lng, 
      rideForm.dropoff_lat, 
      rideForm.dropoff_lng
    );
    const seats = user.role === 'driver' ? rideForm.seats_available : rideForm.seats_requested;
    return String(calculateFare(distance, seats));
  };

  const useMyLocationForPickup = () => {
    if (userLocation) {
      setFilterPickupCoords({ lat: userLocation.latitude, lng: userLocation.longitude });
      setFilterPickup('My Current Location');
      toast.success('Using your current location for pickup filter');
    } else {
      toast.error('Location not available');
    }
  };

  const fetchRides = async () => {
    try {
      const response = await axios.get(`${API}/rides/all-available`);
      setRides(response.data);
    } catch (error) {
      toast.error('Failed to fetch rides');
    } finally {
      setLoading(false);
    }
  };

  const handlePostRide = async (e) => {
    e.preventDefault();
    
    // Validate addresses
    if (!rideForm.pickup_address.trim() || !rideForm.dropoff_address.trim()) {
      toast.error('Please enter both pickup and dropoff addresses');
      return;
    }

    // Validate scheduled time if ride type is scheduled
    if (rideForm.ride_type === 'scheduled') {
      if (!rideForm.scheduled_time) {
        toast.error('Please select a date and time for scheduled ride');
        return;
      }
      const scheduledDate = new Date(rideForm.scheduled_time);
      const now = new Date();
      if (scheduledDate <= now) {
        toast.error('Scheduled time must be in the future');
        return;
      }
    }
    
    try {
      // Prepare form data with custom_fare if provided
      const formData = { ...rideForm };
      if (rideForm.custom_price && parseInt(rideForm.custom_price) > 0) {
        formData.custom_fare = parseInt(rideForm.custom_price); // Whole number only
      }
      
      await axios.post(`${API}/rides/request`, formData);
      toast.success('Ride posted successfully!');
      setShowPostRide(false);
      fetchRides();
      // Reset form
      setRideForm({
        pickup_address: '',
        pickup_lat: 37.6872,
        pickup_lng: -97.3301,
        dropoff_address: '',
        dropoff_lat: 37.7200,
        dropoff_lng: -97.2800,
        ride_type: 'now',
        scheduled_time: '',
        notes: '',
        seats_available: 1,
        seats_requested: 1,
        custom_price: '',
      });
      setPickupSuggestions([]);
      setDropoffSuggestions([]);
      setShowPickupSuggestions(false);
      setShowDropoffSuggestions(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post ride');
    }
  };

  const searchLocation = async (query, type) => {
    if (!query || query.length < 3) {
      if (type === 'pickup') setPickupSuggestions([]);
      else setDropoffSuggestions([]);
      return;
    }

    try {
      // Get user's current location for proximity-based search
      const centerLat = userLocation?.latitude || mapCenter[0] || 37.6872;
      const centerLng = userLocation?.longitude || mapCenter[1] || -97.3301;
      
      // Create a ~50 mile (~80km) bounding box around user's location
      const latOffset = 0.75; // ~50 miles north/south
      const lngOffset = 0.9;  // ~50 miles east/west
      
      // viewbox format: left,top,right,bottom (minLon, maxLat, maxLon, minLat)
      const viewbox = `${centerLng - lngOffset},${centerLat + latOffset},${centerLng + lngOffset},${centerLat - latOffset}`;
      
      const response = await axios.get(`https://nominatim.openstreetmap.org/search`, {
        params: {
          q: query,
          format: 'json',
          limit: 5,
          addressdetails: 1,
          viewbox: viewbox,
          bounded: 1, // Strictly limit results to within the viewbox
        },
        headers: {
          'User-Agent': 'vgoride-App/1.0'
        }
      });

      let suggestions = response.data.map(item => ({
        display_name: item.display_name,
        lat: parseFloat(item.lat),
        lon: parseFloat(item.lon),
      }));

      // If no results found in the bounded area, search without bounds as fallback
      if (suggestions.length === 0) {
        const fallbackResponse = await axios.get(`https://nominatim.openstreetmap.org/search`, {
          params: {
            q: query,
            format: 'json',
            limit: 5,
            addressdetails: 1,
          },
          headers: {
            'User-Agent': 'vgoride-App/1.0'
          }
        });
        suggestions = fallbackResponse.data.map(item => ({
          display_name: item.display_name,
          lat: parseFloat(item.lat),
          lon: parseFloat(item.lon),
        }));
      }

      if (type === 'pickup') {
        setPickupSuggestions(suggestions);
        setShowPickupSuggestions(true);
      } else {
        setDropoffSuggestions(suggestions);
        setShowDropoffSuggestions(true);
      }
    } catch (error) {
      console.error('Failed to search location:', error);
    }
  };

  const handlePickupChange = (value) => {
    setRideForm({ ...rideForm, pickup_address: value });
    const timeoutId = setTimeout(() => searchLocation(value, 'pickup'), 500);
    return () => clearTimeout(timeoutId);
  };

  const handleDropoffChange = (value) => {
    setRideForm({ ...rideForm, dropoff_address: value });
    const timeoutId = setTimeout(() => searchLocation(value, 'dropoff'), 500);
    return () => clearTimeout(timeoutId);
  };

  const selectPickupSuggestion = (suggestion) => {
    setRideForm({
      ...rideForm,
      pickup_address: suggestion.display_name,
      pickup_lat: suggestion.lat,
      pickup_lng: suggestion.lon,
    });
    setShowPickupSuggestions(false);
    setPickupSuggestions([]);
    setMapCenter([suggestion.lat, suggestion.lon]);
  };

  const selectDropoffSuggestion = (suggestion) => {
    setRideForm({
      ...rideForm,
      dropoff_address: suggestion.display_name,
      dropoff_lat: suggestion.lat,
      dropoff_lng: suggestion.lon,
    });
    setShowDropoffSuggestions(false);
    setDropoffSuggestions([]);
  };

  const handleLocateMe = () => {
    if (navigator.geolocation) {
      toast.info('Getting your location...');
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          
          try {
            const response = await axios.get(`https://nominatim.openstreetmap.org/reverse`, {
              params: {
                lat: latitude,
                lon: longitude,
                format: 'json',
              },
              headers: {
                'User-Agent': 'vgoride-App/1.0'
              }
            });
            
            setRideForm({
              ...rideForm,
              pickup_address: response.data.display_name,
              pickup_lat: latitude,
              pickup_lng: longitude,
            });
            setMapCenter([latitude, longitude]);
            toast.success('Location detected!');
          } catch (error) {
            setRideForm({
              ...rideForm,
              pickup_lat: latitude,
              pickup_lng: longitude,
              pickup_address: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
            });
            setMapCenter([latitude, longitude]);
            toast.success('Location set!');
          }
        },
        (error) => {
          toast.error('Unable to get your location');
        }
      );
    } else {
      toast.error('Geolocation is not supported by your browser');
    }
  };

  const handleJoinRide = async () => {
    if (!selectedRide || seatsToJoin < 1) return;
    
    if (seatsToJoin > selectedRide.seats_available) {
      toast.error(`Only ${selectedRide.seats_available} seats available`);
      return;
    }
    
    try {
      await axios.post(`${API}/rides/${selectedRide.id}/accept?seats_needed=${seatsToJoin}`);
      toast.success('Successfully joined the ride! Check your notification center for details.', {
        duration: 5000,
        action: {
          label: 'Go to Dashboard',
          onClick: () => navigate('/rider')
        }
      });
      setShowJoinModal(false);
      setSelectedRide(null);
      setSeatsToJoin(1);
      fetchRides();
      
      // Redirect to dashboard after a short delay so user sees the toast
      setTimeout(() => {
        navigate('/rider');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join ride');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      requested: 'text-yellow-400 bg-yellow-500/20',
      accepted: 'text-blue-400 bg-blue-500/20',
      en_route: 'text-purple-400 bg-purple-500/20',
      arrived: 'text-green-400 bg-green-500/20',
      in_progress: 'text-neon-cyan bg-cyan-500/20',
      completed: 'text-green-500 bg-green-600/20',
      cancelled: 'text-red-400 bg-red-500/20',
    };
    return colors[status] || 'text-white bg-white/10';
  };

  const formatStatus = (status) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-dark-surface">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="font-outfit font-bold text-2xl">Rides Available</h1>
                <p className="text-sm text-white/60">
                  {filteredRides.length} of {rides.length} rides 
                  {(filterPickupCoords || filterDropoffCoords) && ' (filtered)'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowPostRide(true)}
              className="flex items-center gap-2 bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-full font-bold px-6 py-3 shadow-[0_0_15px_rgba(0,224,255,0.4)] transition-all hover:scale-105"
              data-testid="post-ride-btn"
            >
              <Plus className="w-5 h-5" />
              {user.role === 'driver' ? 'Post a Ride' : 'Post a Request'}
            </button>
          </div>
          
          {/* Prominent Search Bar */}
          <div className="bg-dark-bg rounded-2xl p-4 border border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <Search className="w-5 h-5 text-neon-cyan" />
              <span className="text-sm font-medium text-white">Search for Rides</span>
              {searchDistance > 0 && (
                <span className="ml-auto text-xs bg-neon-cyan/20 text-neon-cyan px-2 py-1 rounded-full">
                  {searchDistance.toFixed(0)} miles trip
                </span>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* From Location */}
              <div className="relative">
                <label className="block text-xs text-white/60 mb-1">From (Pickup Area)</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neon-cyan" />
                    <input
                      type="text"
                      value={filterPickup}
                      onChange={(e) => handleFilterPickupChange(e.target.value)}
                      onFocus={() => filterPickup.length >= 3 && setShowFilterPickupSuggestions(true)}
                      placeholder="Enter pickup city or address..."
                      className="w-full bg-dark-subtle border border-white/10 focus:border-neon-cyan rounded-xl p-3 pl-10 text-sm text-white"
                      data-testid="search-from-input"
                    />
                    {showFilterPickupSuggestions && filterPickupSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-dark-surface border border-white/10 rounded-lg overflow-hidden shadow-xl max-h-48 overflow-y-auto">
                        {filterPickupSuggestions.map((suggestion, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => selectFilterPickupSuggestion(suggestion)}
                            className="w-full text-left px-3 py-2 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                          >
                            <span className="text-xs text-white/90 line-clamp-2">{suggestion.display_name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={useMyLocationForPickup}
                    className="p-3 bg-dark-subtle border border-white/10 hover:border-neon-cyan rounded-xl transition-colors"
                    title="Use my location"
                    data-testid="use-my-location-btn"
                  >
                    <Locate className="w-4 h-4 text-neon-cyan" />
                  </button>
                </div>
              </div>
              
              {/* Arrow indicator */}
              <div className="hidden lg:flex items-end justify-center pb-3">
                <ArrowRight className="w-6 h-6 text-white/40" />
              </div>
              
              {/* To Location */}
              <div className="relative">
                <label className="block text-xs text-white/60 mb-1">To (Dropoff Area)</label>
                <div className="relative">
                  <Navigation className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neon-purple" />
                  <input
                    type="text"
                    value={filterDropoff}
                    onChange={(e) => handleFilterDropoffChange(e.target.value)}
                    onFocus={() => filterDropoff.length >= 3 && setShowFilterDropoffSuggestions(true)}
                    placeholder="Enter destination city or address..."
                    className="w-full bg-dark-subtle border border-white/10 focus:border-neon-cyan rounded-xl p-3 pl-10 text-sm text-white"
                    data-testid="search-to-input"
                  />
                  {showFilterDropoffSuggestions && filterDropoffSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-dark-surface border border-white/10 rounded-lg overflow-hidden shadow-xl max-h-48 overflow-y-auto">
                      {filterDropoffSuggestions.map((suggestion, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => selectFilterDropoffSuggestion(suggestion)}
                          className="w-full text-left px-3 py-2 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                        >
                          <span className="text-xs text-white/90 line-clamp-2">{suggestion.display_name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Radius & Actions */}
              <div>
                <label className="block text-xs text-white/60 mb-1">Search Radius: {filterRadius} mi</label>
                <div className="flex gap-2">
                  <input
                    type="range"
                    min="10"
                    max="150"
                    value={filterRadius}
                    onChange={(e) => setFilterRadius(parseInt(e.target.value))}
                    className="flex-1 accent-neon-cyan h-11"
                    data-testid="filter-radius-slider"
                  />
                  {(filterPickupCoords || filterDropoffCoords) && (
                    <button
                      onClick={clearFilters}
                      className="px-4 py-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-xl text-sm transition-colors"
                      data-testid="clear-filters-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Active Filter Tags */}
            {(filterPickupCoords || filterDropoffCoords) && (
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-white/10">
                {filterPickupCoords && (
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-neon-cyan/20 text-neon-cyan rounded-full text-xs">
                    <MapPin className="w-3 h-3" />
                    From: {filterPickup.split(',')[0]}
                    <button 
                      onClick={() => { setFilterPickup(''); setFilterPickupCoords(null); }}
                      className="ml-1 hover:text-white"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filterDropoffCoords && (
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-neon-purple/20 text-neon-purple rounded-full text-xs">
                    <Navigation className="w-3 h-3" />
                    To: {filterDropoff.split(',')[0]}
                    <button 
                      onClick={() => { setFilterDropoff(''); setFilterDropoffCoords(null); }}
                      className="ml-1 hover:text-white"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-20">
            <div className="text-white/60">Loading rides...</div>
          </div>
        ) : filteredRides.length === 0 ? (
          <div>
            {/* Long Distance Suggestions */}
            {showLongDistanceInfo && suggestedRides.length > 0 && (
              <div className="mb-8">
                <div className="bg-gradient-to-r from-neon-purple/20 to-neon-cyan/20 rounded-2xl p-6 border border-white/10">
                  <div className="flex items-start gap-3 mb-4">
                    <AlertCircle className="w-6 h-6 text-neon-cyan flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-outfit font-bold text-lg text-white">Long Distance Trip ({searchDistance.toFixed(0)} miles)</h3>
                      <p className="text-white/70 text-sm">
                        No direct rides found. Here are rides going near your destination that you might be interested in:
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {suggestedRides.slice(0, 4).map((ride) => (
                      <div key={ride.id} className="bg-dark-surface/80 rounded-xl p-4 border border-white/5">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-neon-purple bg-neon-purple/20 px-2 py-1 rounded-full">
                            {ride.distanceToDestination.toFixed(0)} mi from your destination
                          </span>
                          <span className="text-lg font-bold text-neon-cyan">${ride.fare}</span>
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3 h-3 text-neon-cyan" />
                            <span className="text-white/80 truncate">{ride.pickup_address.split(',')[0]}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Navigation className="w-3 h-3 text-neon-purple" />
                            <span className="text-white/80 truncate">{ride.dropoff_address.split(',')[0]}</span>
                          </div>
                        </div>
                        {user.role === 'rider' && ride.posted_by === 'driver' && (
                          <button
                            onClick={() => {
                              setSelectedRide(ride);
                              setSeatsToJoin(1);
                              setShowJoinModal(true);
                            }}
                            className="w-full mt-3 bg-neon-cyan/20 text-neon-cyan hover:bg-neon-cyan hover:text-black rounded-lg font-medium py-2 text-sm transition-all"
                          >
                            Request to Join
                          </button>
                        )}
                        {user.role === 'driver' && ride.posted_by === 'rider' && (
                          <button
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/rides/${ride.id}/accept`);
                                toast.success('Ride request accepted!');
                                fetchRides();
                                navigate('/driver');
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Failed to accept ride');
                              }
                            }}
                            className="w-full mt-3 bg-green-500/20 text-green-400 hover:bg-green-500 hover:text-white rounded-lg font-medium py-2 text-sm transition-all"
                          >
                            Accept Request
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {suggestedRides.length > 4 && (
                    <p className="text-center text-white/50 text-sm mt-4">
                      +{suggestedRides.length - 4} more rides available near your destination
                    </p>
                  )}
                </div>
              </div>
            )}
            
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <h2 className="font-outfit font-bold text-2xl mb-2">
                {rides.length === 0 ? 'No Rides Posted Yet' : 'No Exact Matches Found'}
              </h2>
              <p className="text-white/60 mb-6">
                {rides.length === 0 
                  ? 'Hang on tight. New rides will appear here.'
                  : showLongDistanceInfo 
                    ? 'Check out the suggested rides above or post your own request.'
                    : 'Try adjusting your search radius or locations.'}
              </p>
              {rides.length > 0 && (filterPickupCoords || filterDropoffCoords) && (
                <button
                  onClick={clearFilters}
                  className="bg-white/10 hover:bg-white/20 text-white rounded-full font-medium px-6 py-3 transition-colors mr-3"
                >
                  Clear Filters
                </button>
              )}
              <button
                onClick={() => setShowPostRide(true)}
                className="bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-full font-bold px-8 py-4 shadow-[0_0_15px_rgba(0,224,255,0.4)] transition-all hover:scale-105"
              >
                {user.role === 'driver' ? 'Post Your First Ride' : 'Post Your First Request'}
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRides.map((ride) => (
              <div key={ride.id} className="bg-dark-surface rounded-2xl p-6 border border-white/5 hover:border-white/10 transition-colors">
                <div className="flex items-start justify-between mb-4">
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(ride.status)}`}>
                    {formatStatus(ride.status)}
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-white/60">Fare</div>
                    <div className="text-2xl font-bold text-neon-cyan">${ride.fare}</div>
                  </div>
                </div>

                {ride.ride_type === 'scheduled' && ride.scheduled_time && (
                  <div className="flex items-center gap-2 mb-3 text-sm text-neon-purple">
                    <Clock className="w-4 h-4" />
                    <span>{new Date(ride.scheduled_time).toLocaleString()}</span>
                  </div>
                )}

                <div className="space-y-3 mb-4">
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-neon-cyan mt-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-white/60 mb-1">Pickup</div>
                      <div className="text-sm text-white truncate">{ride.pickup_address}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Navigation className="w-4 h-4 text-neon-purple mt-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-white/60 mb-1">Dropoff</div>
                      <div className="text-sm text-white truncate">{ride.dropoff_address}</div>
                    </div>
                  </div>
                </div>

                <div className="pt-3 border-t border-white/10">
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <div className="text-white/60">Distance</div>
                      <div className="font-mono">{ride.distance.toFixed(1)} miles</div>
                    </div>
                    <div>
                      <div className="text-white/60">ETA</div>
                      <div className="font-mono">{ride.eta} min</div>
                    </div>
                    <div>
                      <div className="text-white/60">Seats</div>
                      <div className="font-mono font-bold text-neon-cyan">
                        {ride.posted_by === 'driver' ? (
                          <span>{ride.seats_available}/{ride.total_seats}</span>
                        ) : (
                          <span>{ride.seats_requested} needed</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {ride.driver_name && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10">
                    <User className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-white/80">Driver: {ride.driver_name}</span>
                  </div>
                )}

                {ride.rider_name && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10">
                    <User className="w-4 h-4 text-blue-400" />
                    <span className="text-sm text-white/80">Rider: {ride.rider_name}</span>
                  </div>
                )}

                {/* Join Button for Riders on Driver-posted rides */}
                {user.role === 'rider' && ride.posted_by === 'driver' && ride.seats_available > 0 && (
                  <button
                    onClick={() => {
                      setSelectedRide(ride);
                      setSeatsToJoin(1);
                      setShowJoinModal(true);
                    }}
                    className="w-full mt-3 bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-full font-bold py-3 shadow-[0_0_15px_rgba(0,224,255,0.4)] transition-all hover:scale-105"
                    data-testid={`join-ride-${ride.id}`}
                  >
                    Join This Ride
                  </button>
                )}

                {/* Accept Button for Drivers on Rider-posted requests */}
                {user.role === 'driver' && ride.posted_by === 'rider' && ride.status === 'requested' && !ride.driver_id && (
                  <button
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/rides/${ride.id}/accept`);
                        toast.success('Ride request accepted! Check your dashboard.');
                        fetchRides();
                        navigate('/driver');
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to accept ride');
                      }
                    }}
                    className="w-full mt-3 bg-green-500 text-white hover:bg-green-600 rounded-full font-bold py-3 shadow-[0_0_15px_rgba(34,197,94,0.4)] transition-all hover:scale-105"
                    data-testid={`accept-ride-${ride.id}`}
                  >
                    Accept This Request
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Post Ride Modal - Full Featured */}
      {showPostRide && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6 overflow-y-auto">
          <div className="bg-dark-bg rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            {/* Map View */}
            <div className="h-64 relative">
              <MapContainer
                center={mapCenter}
                zoom={13}
                style={{ height: '100%', width: '100%' }}
                zoomControl={true}
              >
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                <MapUpdater center={mapCenter} />
                
                {rideForm.pickup_address && (
                  <Marker position={[rideForm.pickup_lat, rideForm.pickup_lng]}>
                    <Popup>Pickup: {rideForm.pickup_address}</Popup>
                  </Marker>
                )}
                
                {rideForm.dropoff_address && (
                  <Marker position={[rideForm.dropoff_lat, rideForm.dropoff_lng]}>
                    <Popup>Dropoff: {rideForm.dropoff_address}</Popup>
                  </Marker>
                )}
              </MapContainer>
            </div>

            {/* Form */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-outfit font-bold text-2xl text-white">
                  {user.role === 'driver' ? 'Post a Ride' : 'Post a Ride Request'}
                </h2>
                <button
                  onClick={() => {
                    setShowPostRide(false);
                    setPickupSuggestions([]);
                    setDropoffSuggestions([]);
                  }}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>

              <form onSubmit={handlePostRide} className="space-y-5">
                {/* Ride Type */}
                <div>
                  <label className="block text-sm mb-3 text-white/80">
                    {user.role === 'driver' ? 'When do you want to offer this ride?' : 'When do you need this ride?'}
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setRideForm({ ...rideForm, ride_type: 'now', scheduled_time: '' })}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        rideForm.ride_type === 'now'
                          ? 'border-neon-cyan bg-neon-cyan/10 text-neon-cyan'
                          : 'border-white/10 text-white/60 hover:border-white/20'
                      }`}
                    >
                      <Clock className="w-6 h-6 mx-auto mb-2" />
                      <div className="font-medium">Now</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRideForm({ ...rideForm, ride_type: 'scheduled' })}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        rideForm.ride_type === 'scheduled'
                          ? 'border-neon-cyan bg-neon-cyan/10 text-neon-cyan'
                          : 'border-white/10 text-white/60 hover:border-white/20'
                      }`}
                    >
                      <Calendar className="w-6 h-6 mx-auto mb-2" />
                      <div className="font-medium">Schedule</div>
                    </button>
                  </div>
                </div>

                {/* Scheduled Time */}
                {rideForm.ride_type === 'scheduled' && (
                  <div>
                    <label className="block text-sm mb-2 text-white/80">Select Date & Time</label>
                    <input
                      type="datetime-local"
                      required
                      value={rideForm.scheduled_time}
                      onChange={(e) => setRideForm({ ...rideForm, scheduled_time: e.target.value })}
                      min={new Date().toISOString().slice(0, 16)}
                      className="w-full bg-dark-subtle border-transparent focus:border-neon-cyan rounded-xl p-3 text-white"
                    />
                  </div>
                )}

                {/* Pickup Address */}
                <div className="relative">
                  <label className="block text-sm mb-2 text-white/80 flex items-center justify-between">
                    <span>Pickup Address</span>
                    <button
                      type="button"
                      onClick={handleLocateMe}
                      className="text-xs text-neon-cyan hover:text-[#00B8D4] flex items-center gap-1"
                    >
                      <Locate className="w-3 h-3" />
                      Locate Me
                    </button>
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                    <input
                      type="text"
                      required
                      value={rideForm.pickup_address}
                      onChange={(e) => handlePickupChange(e.target.value)}
                      onFocus={() => rideForm.pickup_address.length >= 3 && setShowPickupSuggestions(true)}
                      className="w-full bg-dark-subtle border-transparent focus:border-neon-cyan rounded-xl p-3 pl-10 text-white"
                      placeholder="Search pickup location..."
                      autoComplete="off"
                    />
                  </div>
                  {showPickupSuggestions && pickupSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-2 bg-dark-surface border border-white/10 rounded-xl overflow-hidden shadow-xl max-h-60 overflow-y-auto">
                      {pickupSuggestions.map((suggestion, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => selectPickupSuggestion(suggestion)}
                          className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                        >
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-neon-cyan mt-1 flex-shrink-0" />
                            <span className="text-sm text-white/90">{suggestion.display_name}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Dropoff Address */}
                <div className="relative">
                  <label className="block text-sm mb-2 text-white/80">Dropoff Address</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                    <input
                      type="text"
                      required
                      value={rideForm.dropoff_address}
                      onChange={(e) => handleDropoffChange(e.target.value)}
                      onFocus={() => rideForm.dropoff_address.length >= 3 && setShowDropoffSuggestions(true)}
                      className="w-full bg-dark-subtle border-transparent focus:border-neon-cyan rounded-xl p-3 pl-10 text-white"
                      placeholder="Search dropoff location..."
                      autoComplete="off"
                    />
                  </div>
                  {showDropoffSuggestions && dropoffSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-2 bg-dark-surface border border-white/10 rounded-xl overflow-hidden shadow-xl max-h-60 overflow-y-auto">
                      {dropoffSuggestions.map((suggestion, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => selectDropoffSuggestion(suggestion)}
                          className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                        >
                          <div className="flex items-start gap-2">
                            <Navigation className="w-4 h-4 text-neon-purple mt-1 flex-shrink-0" />
                            <span className="text-sm text-white/90">{suggestion.display_name}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm mb-2 text-white/80">Notes (Optional)</label>
                  <textarea
                    value={rideForm.notes}
                    onChange={(e) => setRideForm({ ...rideForm, notes: e.target.value })}
                    className="w-full bg-dark-subtle border-transparent focus:border-neon-cyan rounded-xl p-3 text-white"
                    rows="2"
                    placeholder="Any special information about this ride..."
                  />
                </div>

                {/* Seats Selection */}
                <div>
                  <label className="block text-sm mb-2 text-white/80">
                    {user.role === 'driver' ? 'How many seats available?' : 'How many seats needed?'}
                  </label>
                  <select
                    value={user.role === 'driver' ? rideForm.seats_available : rideForm.seats_requested}
                    onChange={(e) => setRideForm({
                      ...rideForm,
                      ...(user.role === 'driver' 
                        ? {seats_available: parseInt(e.target.value)}
                        : {seats_requested: parseInt(e.target.value)}
                      )
                    })}
                    className="w-full bg-dark-subtle border-transparent focus:border-neon-cyan rounded-xl p-3 text-white"
                    required
                  >
                    <option value="1">1 Seat</option>
                    <option value="2">2 Seats</option>
                    <option value="3">3 Seats</option>
                    <option value="4">4 Seats</option>
                    <option value="5">5 Seats</option>
                    <option value="6">6 Seats</option>
                    <option value="7">7 Seats</option>
                  </select>
                  <p className="text-xs text-white/40 mt-2">
                    {user.role === 'driver' 
                      ? 'Select how many passengers can join your ride'
                      : 'Select how many people need a ride (including you)'}
                  </p>
                </div>

                {/* Custom Price */}
                <div>
                  <label className="block text-sm mb-2 text-white/80 flex items-center justify-between">
                    <span>Ride Price ($)</span>
                    {rideForm.custom_price && (
                      <span className="text-xs text-neon-cyan">Editable</span>
                    )}
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neon-cyan z-10" />
                    <input
                      type="number"
                      step="1"
                      min="1"
                      value={rideForm.custom_price}
                      onChange={(e) => setRideForm({ ...rideForm, custom_price: e.target.value })}
                      placeholder="Enter price..."
                      className="w-full bg-dark-subtle border-transparent focus:border-neon-cyan rounded-xl p-3 pl-10 text-white text-lg font-semibold placeholder:text-white/40"
                      data-testid="input-custom-price"
                    />
                  </div>
                  <p className="text-xs text-white/40 mt-2">
                    {rideForm.pickup_lat && rideForm.dropoff_lat ? (
                      <>
                        Estimated distance: {calculateDistance(rideForm.pickup_lat, rideForm.pickup_lng, rideForm.dropoff_lat, rideForm.dropoff_lng).toFixed(1)} miles. 
                        Price auto-calculated. Feel free to adjust if needed.
                      </>
                    ) : (
                      'Enter pickup and dropoff to auto-calculate fare. Default: $1/mile, $3 minimum, +$1 per extra seat.'
                    )}
                  </p>
                </div>

                {/* Submit */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPostRide(false);
                      setPickupSuggestions([]);
                      setDropoffSuggestions([]);
                    }}
                    className="flex-1 bg-white/10 hover:bg-white/20 rounded-full py-4 transition-colors text-white font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-full font-bold py-4 shadow-[0_0_15px_rgba(0,224,255,0.4)] transition-all hover:scale-105"
                  >
                    {user.role === 'driver' ? 'Post Ride' : 'Post Request'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* Join Ride Modal */}
      {showJoinModal && selectedRide && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
          <div className="bg-dark-surface rounded-2xl p-6 w-full max-w-md">
            <h2 className="font-outfit font-bold text-2xl mb-4 text-white">Join This Ride</h2>
            
            <div className="mb-6 p-4 bg-dark-bg rounded-xl">
              <div className="text-sm text-white/60 mb-2">Ride Details</div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-neon-cyan" />
                  <span className="text-white/80">{selectedRide.pickup_address}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Navigation className="w-4 h-4 text-neon-purple" />
                  <span className="text-white/80">{selectedRide.dropoff_address}</span>
                </div>
                <div className="flex items-center gap-2 text-sm pt-2">
                  <User className="w-4 h-4 text-green-400" />
                  <span className="text-white/80">Driver: {selectedRide.driver_name}</span>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm mb-2 text-white/80">
                How many seats do you need?
              </label>
              <select
                value={seatsToJoin}
                onChange={(e) => setSeatsToJoin(parseInt(e.target.value))}
                className="w-full bg-dark-subtle border-transparent focus:border-neon-cyan rounded-xl p-3 text-white"
              >
                {[...Array(Math.min(selectedRide.seats_available, 7))].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1} {i + 1 === 1 ? 'Seat' : 'Seats'}
                  </option>
                ))}
              </select>
              <p className="text-xs text-white/40 mt-2">
                {selectedRide.seats_available} seats available
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowJoinModal(false);
                  setSelectedRide(null);
                  setSeatsToJoin(1);
                }}
                className="flex-1 bg-white/10 hover:bg-white/20 rounded-full py-3 transition-colors text-white font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleJoinRide}
                className="flex-1 bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-full font-bold py-3 shadow-[0_0_15px_rgba(0,224,255,0.4)] transition-all hover:scale-105"
              >
                Join Ride
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}